import React, { lazy, Suspense, Fragment, useContext, useEffect } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { ThemeProvider } from "@material-ui/styles";
import PrivateRouteAuthGuard from "./utils/PrivateRouteAuthGuard";
import UserContext from "contexts/userContext";
import { ClimbingBoxLoader } from "react-spinners";

import { createTheme } from "@material-ui/core/styles";
import { frFR, enUS } from "@material-ui/core/locale";

import {
  LeftSidebar,
  CollapsedSidebar,
  MinimalLayout,
  PresentationLayout,
} from "./layout-blueprints";

import PrivateRoutes from "./utils/PrivateRoutes";
import PrivateRoutesEdit from "./utils/PrivateRoutesEdit";
import PrivateRoutesHistorique from "utils/PrivateRoutesHistorique";
import MissingRoute from "./utils/MissingRoute";
import { themejson } from "theme/theme";

// Pages chargées à la demande (code-splitting)
const PagesLogin = lazy(() => import("./example-pages/PagesLogin"));
const PagesRegister = lazy(() => import("./example-pages/PagesRegister"));
const PagesRecoverPassword = lazy(() =>
  import("./example-pages/PagesRecoverPassword"),
);
const PagesProfile = lazy(() => import("./example-pages/PagesProfile"));
const PagesInvoice = lazy(() => import("./example-pages/PagesInvoice"));
const PagesError404 = lazy(() => import("./example-pages/PagesError404"));
const PagesError500 = lazy(() => import("./example-pages/PagesError500"));
const PagesError505 = lazy(() => import("./example-pages/PagesError505"));
const PagesErrorBlocked = lazy(() =>
  import("./example-pages/PagesErrorBlocked"),
);

const Creation = lazy(() => import("./example-pages/Creation"));
const Visualisation = lazy(() => import("./example-pages/Visualisation"));
const Listebesc = lazy(() => import("./example-pages/Listebesc"));
const Historique = lazy(() => import("./example-pages/Historique"));
const Historiquebesc = lazy(() => import("./example-pages/Historiquebesc"));
const Listebescsociete = lazy(() =>
  import("./example-pages/Listebescsociete"),
);
const Listeredressement = lazy(() =>
  import("./example-pages/Listeredressement"),
);
const Listeexoneration = lazy(() =>
  import("./example-pages/Listeexoneration"),
);
const Listeannulation = lazy(() => import("./example-pages/Listeannulation"));
const Chargeur = lazy(() => import("./example-pages/HelpDesk/Chargeur"));
const GestionDesComptes = lazy(() =>
  import("./example-pages/Configuration/AccountManagement"),
);
const Navire = lazy(() => import("./example-pages/HelpDesk/Navire"));
const CodeSH = lazy(() => import("./example-pages/HelpDesk/CodeSH"));
const Devise = lazy(() => import("./example-pages/HelpDesk/Devise"));
const Fournisseur = lazy(() => import("./example-pages/HelpDesk/Fournisseur"));
const Incoterm = lazy(() => import("./example-pages/HelpDesk/Incoterm"));
const Marque = lazy(() => import("./example-pages/HelpDesk/Marque"));
const Modele = lazy(() => import("./example-pages/HelpDesk/Modele"));
const Pays = lazy(() => import("./example-pages/HelpDesk/Pays"));
const Port = lazy(() => import("./example-pages/HelpDesk/Port"));
const Transporteur = lazy(() =>
  import("./example-pages/HelpDesk/Transporteur"),
);
const Transitaire = lazy(() => import("./example-pages/HelpDesk/Transitaire"));
const Ville = lazy(() => import("./example-pages/HelpDesk/Ville"));
const Approvisionnement = lazy(() =>
  import("./example-pages/Facturation/Approvisionnement/Demande"),
);
const Mouvementmanuel = lazy(() =>
  import("./example-pages/Facturation/Approvisionnement/Mouvementmanuel"),
);
const DemandeIntermediaire = lazy(() =>
  import("./example-pages/DemandeIntermediaire/index.js"),
);
const Listeapprovisionnement = lazy(() =>
  import("./example-pages/Facturation/Approvisionnement/Listing"),
);
const Listingredressement = lazy(() =>
  import("./example-pages/Facturation/Listingredressement"),
);
const SelectionTypeBesc = lazy(() =>
  import("./example-pages/Besc/SelectionTypeBesc"),
);
const DashboardShipper = lazy(() =>
  import("./example-pages/DashboardShipper"),
);
const Statistiquebesc = lazy(() =>
  import("./example-pages/Statistiquebesc/Chargeur"),
);
const Statistiquebescoperation = lazy(() =>
  import("./example-pages/Statistiquebesc/Operation"),
);
const Statistiquebescstat = lazy(() =>
  import("./example-pages/Statistiquebesc/Stat"),
);
const Performances = lazy(() =>
  import("./example-pages/Statistiquebesc/Performances"),
);
const Mouvementbesc = lazy(() =>
  import("./example-pages/Mouvementbesc/Chargeur"),
);
const Mouvementbescoperation = lazy(() =>
  import("./example-pages/Mouvementbesc/Operation"),
);
const Statistiquebescreconciliation = lazy(() =>
  import("./example-pages/Statistiquebesc/Reconciliation"),
);
const DashboardDefault = lazy(() =>
  import("./example-pages/DashboardDefault"),
);
const LandingPage = lazy(() => import("./example-pages/LandingPage"));
const Intermediaires = lazy(() => import("./example-pages/Intermediaires"));
const Tarification = lazy(() => import("./example-pages/Tarification"));
const ListGroups = lazy(() => import("./example-pages/ListGroups"));

const Routes = () => {
  const { role, setRole, locale, setLocale } = useContext(UserContext);
  const cl = locale === "en" ? enUS : frFR;
  useEffect(() => {
    console.log("locale : ", locale, " cl : ", cl);
  }, []);
  const location = useLocation();
  const MuiTheme = createTheme(themejson, cl);
  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.99,
    },
    in: {
      opacity: 1,
      scale: 1,
    },
    out: {
      opacity: 0,
      scale: 1.01,
    },
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.4,
  };

  const SuspenseLoading = () => {
    return (
      <Fragment>
        <div className="d-flex align-items-center flex-column vh-100 justify-content-center text-center py-3">
          <div className="d-flex align-items-center flex-column px-4">
            <ClimbingBoxLoader color={"#5383ff"} loading={true} />
          </div>
          <div className="text-muted font-size-xl text-center pt-3">
            Chargement en cours...
          </div>
        </div>
      </Fragment>
    );
  };
  return (
    <ThemeProvider theme={MuiTheme}>
      <AnimatePresence>
        <Suspense fallback={<SuspenseLoading />}>
          <Switch>
            <Redirect exact from="/" to="/Accueil" />
            <Route path={["/Accueil"]}>
              <PresentationLayout>
                <Switch location={location} key={location.pathname}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Route path="/Accueil" component={LandingPage} />
                  </motion.div>
                </Switch>
              </PresentationLayout>
            </Route>

            {/* <Route
              path={[
                "/ApplicationsCalendar",
                "/ApplicationsChat",
                "/ApplicationsContacts",
                "/ApplicationsFileManager",
                "/ApplicationsMail"
              ]}
            >
              <CollapsedSidebar>
                <Switch location={location} key={location.pathname}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Route
                      path="/ApplicationsCalendar"
                      component={ApplicationsCalendar}
                    />
                    <Route
                      path="/ApplicationsChat"
                      component={ApplicationsChat}
                    />
                    <Route
                      path="/ApplicationsContacts"
                      component={ApplicationsContacts}
                    />
                    <Route
                      path="/ApplicationsFileManager"
                      component={ApplicationsFileManager}
                    />
                    <Route
                      path="/ApplicationsMail"
                      component={ApplicationsMail}
                    />
                  </motion.div>
                </Switch>
              </CollapsedSidebar>
            </Route> */}

            <Route
              path={[
                "/PagesLogin",
                "/Login",
                "/PagesRecoverPassword",
                "/PagesError404",
                "/PagesError500",
                "/PagesError505",
                "/PagesErrorBlocked",
                "/Intermediaires",
                "/Tarification",
              ]}
            >
              <MinimalLayout>
                <Switch location={location} key={location.pathname}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <Route path="/Intermediaires" component={Intermediaires} />
                    <Route path="/Tarification" component={Tarification} />
                    <Route path="/PagesLogin" component={PagesLogin} />
                    <Route path="/Login" component={PagesRegister} />
                    <Route
                      path="/PagesRecoverPassword"
                      component={PagesRecoverPassword}
                    />
                    <Route path="/PagesError404" component={PagesError404} />
                    <Route path="/PagesError500" component={PagesError500} />
                    <Route path="/PagesError505" component={PagesError505} />
                    <Route
                      path="/PagesErrorBlocked"
                      component={PagesErrorBlocked}
                    />
                  </motion.div>
                </Switch>
              </MinimalLayout>
            </Route>

            <Route
              path={[
                // "/DashboardDefault",
                // "/DashboardAnalytics",
                // "/DashboardSales",
                // "/DashboardReports",
                // "/DashboardRealEstate",
                // "/DashboardServerStatus",
                // "/DashboardProjects",
                // "/DashboardHelpdesk",
                // "/DashboardCustomers",
                // "/DashboardCryptocurrency",
                // "/DashboardCrmManager",
                // "/DashboardContent",
                // "/ApplicationsProjects",
                // "/ApplicationsWidgets",
                "/Profile",
                // "/PagesInvoice",
                // "/Buttons",
                // "/Dropdowns",
                // "/NavigationMenus",
                // "/Avatars",
                // "/ProgressBars",
                // "/Ratings",
                // "/Pagination",
                // "/Ribbons",
                // "/Scrollable",
                // "/Badges",
                // "/Icons",
                // "/Timelines",
                // "/UtilitiesHelpers",
                // "/Cards1",
                // "/Cards2",
                // "/Cards3",
                // "/Cards4",
                // "/Cards5",
                // "/Cards6",
                // "/Cards7",
                // "/Cards8",
                // "/Cards9",
                // "/Cards10",
                // "/Cards11",
                // "/Cards12",
                // "/FeatureSections",
                // "/IconBoxes",
                // "/PricingTables",
                // "/HeaderSections",
                // "/TestimonialsSections",
                // "/BlogSections",
                // "/PartnersSections",
                // "/CtaBoxes",
                // "/FooterSections",
                // "/HeroSections",
                // "/Accordions",
                // "/Modals",
                "/Notifications",
                // "/DragDrop",
                // "/Calendars",
                // "/Carousels",
                // "/Popovers",
                // "/TreeView",
                // "/Tabs",
                // "/GuidedTours",
                // "/ImageCrop",
                // "/LoadingIndicators",
                // "/Countup",
                // "/ContextMenus",
                // "/RegularTables1",
                // "/RegularTables2",
                // "/RegularTables3",
                // "/RegularTables4",
                // "/RegularTables5",
                // "/DynamicTables2",
                // "/FormsControls",
                // "/FormsWizard",
                // "/FormsClipboard",
                // "/FormsColorpicker",
                // "/FormsDatepicker",
                // "/FormsDualListbox",
                // "/FormsInputMask",
                // "/FormsInputSelect",
                // "/FormsSlider",
                // "/FormsTextareaAutosize",
                // "/FormsTimepicker",
                // "/FormsToggleSwitch",
                // "/FormsTypeahead",
                // "/FormsUpload",
                // "/FormsValidation",
                // "/FormsWysiwygEditor",
                // "/ApexCharts",
                // "/GaugesCharts",
                // "/ChartjsCharts",
                // "/SparklinesCharts",
                // "/Maps",
                // "/ListGroups",
                "/Creation",
                "/Show",
                "/Listebesc",
                "/Historiquebesc",
                "/Listebescsociete",
                "/Listeredressement",
                "/Listeexoneration",
                "/Listeannulation",
                "/SelectBesc",
                "/Edit",
                "/History",
                "/Chargeur",
                "/GestionDesComptes",
                "/Navire",
                "/CodeSH",
                "/Devise",
                "/Fournisseur",
                "/Incoterm",
                "/Marque",
                "/Modele",
                "/Pays",
                "/Port",
                "/Transporteur",
                "/Transitaire",
                "/Ville",
                "/Paiements",
                "/Approvisionnement",
                "/Mouvementmanuel",
                "/Listeapprovisionnement",
                "/Listingredressement",
                "/DashboardShipper",
                "/DemandeIntermediaire",
                "/Statistiquebesc",
                "/Statistiquebescoperation",
                "/Statistiques",
                "/Mouvementbesc",
                "/Mouvementbescoperation",
                "/Performances",
                "/Reconciliation",
              ]}
            >
              <LeftSidebar>
                <Switch location={location} key={location.pathname}>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    {/* <Route
                      path="/DashboardDefault"
                      component={DashboardDefault}
                    />
                    <Route
                      path="/DashboardAnalytics"
                      component={DashboardAnalytics}
                    />
                    <Route path="/DashboardSales" component={DashboardSales} />
                    <Route
                      path="/DashboardReports"
                      component={DashboardReports}
                    />
                    <Route
                      path="/DashboardRealEstate"
                      component={DashboardRealEstate}
                    />
                    <Route
                      path="/DashboardServerStatus"
                      component={DashboardServerStatus}
                    />
                    <Route
                      path="/DashboardProjects"
                      component={DashboardProjects}
                    />
                    <Route
                      path="/DashboardHelpdesk"
                      component={DashboardHelpdesk}
                    />
                    <Route
                      path="/DashboardCustomers"
                      component={DashboardCustomers}
                    />
                    <Route
                      path="/DashboardCryptocurrency"
                      component={DashboardCryptocurrency}
                    />
                    <Route
                      path="/DashboardCrmManager"
                      component={DashboardCrmManager}
                    />
                    <Route
                      path="/DashboardContent"
                      component={DashboardContent}
                    />
                    <Route
                      path="/ApplicationsProjects"
                      component={ApplicationsProjects}
                    />
                    <Route
                      path="/ApplicationsWidgets"
                      component={ApplicationsWidgets}
                    /> */}
                    <Route path="/Profile" component={PagesProfile} />
                    {/* <Route path="/PagesInvoice" component={PagesInvoice} />
                    <Route path="/Buttons" component={Buttons} />
                    <Route path="/Dropdowns" component={Dropdowns} />
                    <Route
                      path="/NavigationMenus"
                      component={NavigationMenus}
                    />
                    <Route path="/Avatars" component={Avatars} />
                    <Route path="/ProgressBars" component={ProgressBars} />
                    <Route path="/Ratings" component={Ratings} />
                    <Route path="/Pagination" component={Pagination} />
                    <Route path="/Ribbons" component={Ribbons} />
                    <Route path="/Scrollable" component={Scrollable} />
                    <Route path="/Badges" component={Badges} />
                    <Route path="/Icons" component={Icons} />
                    <Route path="/Timelines" component={Timelines} />
                    <Route
                      path="/UtilitiesHelpers"
                      component={UtilitiesHelpers}
                    />
                    <Route path="/Cards1" component={Cards1} />
                    <Route path="/Cards2" component={Cards2} />
                    <Route path="/Cards3" component={Cards3} />
                    <Route path="/Cards4" component={Cards4} />
                    <Route path="/Cards5" component={Cards5} />
                    <Route path="/Cards6" component={Cards6} />
                    <Route path="/Cards7" component={Cards7} />
                    <Route path="/Cards8" component={Cards8} />
                    <Route path="/Cards9" component={Cards9} />
                    <Route path="/Cards10" component={Cards10} />
                    <Route path="/Cards11" component={Cards11} />
                    <Route path="/Cards12" component={Cards12} />
                    <Route
                      path="/FeatureSections"
                      component={FeatureSections}
                    />
                    <Route path="/IconBoxes" component={IconBoxes} />
                    <Route path="/PricingTables" component={PricingTables} />
                    <Route path="/HeaderSections" component={HeaderSections} />
                    <Route
                      path="/TestimonialsSections"
                      component={TestimonialsSections}
                    />
                    <Route path="/BlogSections" component={BlogSections} />
                    <Route
                      path="/PartnersSections"
                      component={PartnersSections}
                    />
                    <Route path="/CtaBoxes" component={CtaBoxes} />
                    <Route path="/FooterSections" component={FooterSections} />
                    <Route path="/HeroSections" component={HeroSections} />
                    <Route path="/Accordions" component={Accordions} />
                    <Route path="/Modals" component={Modals} />
                    <Route path="/Notifications" component={Notifications} />
                    <Route path="/DragDrop" component={DragDrop} />
                    <Route path="/Calendars" component={Calendars} />
                    <Route path="/Carousels" component={Carousels} />
                    <Route path="/Popovers" component={Popovers} />
                    <Route path="/TreeView" component={TreeView} />
                    <Route path="/Tabs" component={Tabs} />
                    <Route path="/GuidedTours" component={GuidedTours} />
                    <Route path="/ImageCrop" component={ImageCrop} />
                    <Route
                      path="/LoadingIndicators"
                      component={LoadingIndicators}
                    />
                    <Route path="/Countup" component={Countup} />
                    <Route path="/ContextMenus" component={ContextMenus} />
                    <Route path="/RegularTables1" component={RegularTables1} />
                    <Route path="/RegularTables2" component={RegularTables2} />
                    <Route path="/RegularTables3" component={RegularTables3} />
                    <Route path="/RegularTables4" component={RegularTables4} />
                    <Route path="/RegularTables5" component={RegularTables5} />
                    <Route path="/DynamicTables2" component={DynamicTables2} />
                    <Route path="/FormsControls" component={FormsControls} />
                    <Route path="/FormsWizard" component={FormsWizard} />
                    <Route path="/FormsClipboard" component={FormsClipboard} />
                    <Route
                      path="/FormsColorpicker"
                      component={FormsColorpicker}
                    />
                    <Route
                      path="/FormsDatepicker"
                      component={FormsDatepicker}
                    />
                    <Route
                      path="/FormsDualListbox"
                      component={FormsDualListbox}
                    />
                    <Route path="/FormsInputMask" component={FormsInputMask} />
                    <Route
                      path="/FormsInputSelect"
                      component={FormsInputSelect}
                    />
                    <Route path="/FormsSlider" component={FormsSlider} />
                    <Route
                      path="/FormsTextareaAutosize"
                      component={FormsTextareaAutosize}
                    />
                    <Route
                      path="/FormsTimepicker"
                      component={FormsTimepicker}
                    />
                    <Route
                      path="/FormsToggleSwitch"
                      component={FormsToggleSwitch}
                    />
                    <Route path="/FormsTypeahead" component={FormsTypeahead} />
                    <Route path="/FormsUpload" component={FormsUpload} />
                    <Route
                      path="/FormsValidation"
                      component={FormsValidation}
                    />
                    <Route
                      path="/FormsWysiwygEditor"
                      component={FormsWysiwygEditor}
                    />
                    <Route path="/ApexCharts" component={ApexCharts} />
                    <Route path="/GaugesCharts" component={GaugesCharts} />
                    <Route path="/ChartjsCharts" component={ChartjsCharts} />
                    <Route
                      path="/SparklinesCharts"
                      component={SparklinesCharts}
                    />
                    <Route path="/Maps" component={Maps} />
                    <Route path="/ListGroups" component={ListGroups} /> */}

                    {/* {role === "Chargeur" ? (
                                          <Route path="/Creation/" component={Creation} />
                                        ) : (
                                          <Redirect to="/" />
                                        )} */}
                    <PrivateRouteAuthGuard
                      path="/Creation/maritime"
                      component={PrivateRoutes}
                    />
                    <PrivateRouteAuthGuard
                      path="/Edit/"
                      component={PrivateRoutesEdit}
                    />
                    <PrivateRouteAuthGuard
                      path="/History/"
                      component={PrivateRoutesHistorique}
                    />

                    <PrivateRouteAuthGuard
                      path="/Listebesc/:sens?"
                      component={Listebesc}
                    />
                    <PrivateRouteAuthGuard
                      path="/Listeredressement"
                      component={Listeredressement}
                      status={"created"}
                    />
                    <PrivateRouteAuthGuard
                      path="/Listeexoneration"
                      component={Listeexoneration}
                      status={"created"}
                    />
                    <PrivateRouteAuthGuard
                      path="/Listeannulation"
                      component={Listeannulation}
                      status={"created"}
                    />
                    <PrivateRouteAuthGuard
                      path="/Historiquebesc"
                      component={Historiquebesc}
                    />
                    <PrivateRouteAuthGuard
                      path="/Listebescsociete"
                      component={Listebescsociete}
                    />
                    <PrivateRouteAuthGuard
                      path="/SelectBesc"
                      component={SelectionTypeBesc}
                    />
                    <PrivateRouteAuthGuard
                      path="/Show/:sens?/:id"
                      component={Visualisation}
                    />
                    <PrivateRouteAuthGuard
                      path="/DemandeIntermediaire"
                      component={DemandeIntermediaire}
                    />
                    <Route path="/Chargeur" component={Chargeur} />
                    <Route
                      path="/GestionDesComptes"
                      component={GestionDesComptes}
                    />
                    <Route path="/Navire" component={Navire} />
                    <Route path="/CodeSH" component={CodeSH} />
                    <Route path="/Devise" component={Devise} />
                    <Route path="/Fournisseur" component={Fournisseur} />
                    <Route path="/Incoterm" component={Incoterm} />
                    <Route path="/Marque" component={Marque} />
                    <Route path="/Modele" component={Modele} />
                    <Route path="/Pays" component={Pays} />
                    <Route path="/Port" component={Port} />
                    <Route path="/Transporteur" component={Transporteur} />
                    <Route path="/Transitaire" component={Transitaire} />
                    <Route path="/Ville" component={Ville} />
                    <PrivateRouteAuthGuard
                      path="/DashboardShipper"
                      component={DashboardShipper}
                    />
                    <PrivateRouteAuthGuard
                      path="/Approvisionnement"
                      component={Approvisionnement}
                    />
                    <PrivateRouteAuthGuard
                      path="/Mouvementmanuel"
                      component={Mouvementmanuel}
                    />
                    <PrivateRouteAuthGuard
                      path="/Listeapprovisionnement"
                      component={Listeapprovisionnement}
                    />

                    <PrivateRouteAuthGuard
                      path="/Listingredressement"
                      component={Listingredressement}
                    />
                    <PrivateRouteAuthGuard
                      path="/Statistiquebescoperation"
                      component={Statistiquebescoperation}
                    />
                    <PrivateRouteAuthGuard
                      path="/Reconciliation"
                      component={Statistiquebescreconciliation}
                    />
                    <PrivateRouteAuthGuard
                      path="/Statistiques"
                      component={Statistiquebescstat}
                    />
                    <PrivateRouteAuthGuard
                      path="/Performances"
                      component={Performances}
                    />
                    <PrivateRouteAuthGuard
                      path="/Statistiquebesc"
                      component={Statistiquebesc}
                    />
                    <PrivateRouteAuthGuard
                      path="/Mouvementbescoperation"
                      component={Mouvementbescoperation}
                    />
                    <PrivateRouteAuthGuard
                      path="/Mouvementbesc"
                      component={Mouvementbesc}
                    />
                  </motion.div>
                </Switch>
              </LeftSidebar>
            </Route>
            {/* <Route path="*" component={MissingRoute} /> */}
          </Switch>
        </Suspense>
      </AnimatePresence>
    </ThemeProvider>
  );
};

export default Routes;
